import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSearchFilters, setIsClearFilter, setIsViewChanged } from '../redux/slices/search.slice';
import ContentTab from './contentTab';
import AISummary from './AISummary';
import { processContentFilters } from '../utils/searchFunctions';
import { formatNumber } from '../helpers/CommonHelpers';
import ConsentModal from './modals/ConsentModal';
import MembershipPaywallModal from './modals/MembershipPaywallModal';
import { aiApi } from '../api/aiApi';
import he from 'he';

export default function ResultsSection({ filterOpen, isLoadMore, isExpandingSearch, refreshData }) {
    const dispatch = useDispatch();
    const postTypeHeading = useSelector((state) => state.search.fetchPostTypePageHeading);
    const [postPageData, setPostPageData] = useState('');
    const [anlyticsCall, setAnlyticsCall] = useState(false);
    const [dynamicHeading, setDynamicHeading] = useState('');
    const [contentType, setContentType] = useState('');
    const [consentOpen, setConsentOpen] = useState(false);
    const [paywallOpen, setPaywallOpen] = useState(false);
    const [retrySummary, setRetrySummary] = useState(false);
    const searchResults = useSelector((state) => state.search.contentResults);
    const searchOptions = useSelector((state) => state.search.searchOptions);
    const selectedContentTypes = searchOptions?.contentType || [];
    const isOnlyWebinarsAndVideos =
        selectedContentTypes.length > 0 &&
        selectedContentTypes.every((ct) => ct === 'webinars' || ct === 'videos');
    const allFilters = useSelector((state) => state.search.searchFilters);
    const activeFilters = useSelector((state) => state.search.activeFilters);
    const [showMore, setShowMore] = useState(false);
    const contentCount = useSelector((state) => state.search.contentResults?.total || 0);
    const [isSortActive, setIsSortActive] = useState(false);
    const [isGridView, setIsGridView] = useState(false);
    const [gridViewName, setGridViewName] = useState('');
    const isViewChanged = useSelector((state) => state.search.isViewChanged);

    useEffect(() => {
        if (
            searchOptions &&
            searchOptions.contentType &&
            searchOptions.contentType.length === 1 &&
            searchOptions.contentType.includes('infographics')
        ) {
            if (!isViewChanged) {
                setIsGridView(true);
            }
            setGridViewName('infographics');
        } else if (
            searchOptions &&
            searchOptions.contentType &&
            searchOptions.contentType.length === 1 &&
            searchOptions.contentType.includes('kits_collections')
        ) {
            if (!isViewChanged) {
                setIsGridView(true);
            }
            setGridViewName('kits_collections');
        } else if (
            searchOptions &&
            searchOptions.contentType &&
            ((searchOptions.contentType.length === 1 &&
                searchOptions.contentType.includes('webinars')) ||
                isOnlyWebinarsAndVideos)
        ) {
            setIsGridView(true);
            setGridViewName('webinars');
        } else {
            setIsGridView(false);
            setGridViewName('');
            dispatch(setIsViewChanged(false));
        }

        if (
            searchOptions &&
            searchOptions.contentType &&
            (searchOptions.contentType.length === 1 || isOnlyWebinarsAndVideos)
        ) {
            const contentType = isOnlyWebinarsAndVideos ? 'webinars' : searchOptions.contentType[0];
            setContentType(contentType);
            if (contentType === 'analyst_reports') {
                setAnlyticsCall(true);
                const postTypeData = postTypeHeading?.[contentType] || '';
                const parser = new DOMParser();
                const htmlDoc = parser.parseFromString(postTypeData, 'text/html');
                // Extract H1 heading
                const headingElement = htmlDoc.querySelector('h1');
                setDynamicHeading(headingElement ? headingElement.textContent : '');
            } else {
                const postTypeData = postTypeHeading?.[contentType] || '';
                setPostPageData(postTypeData);
                setAnlyticsCall(false);
            }
        } else {
            setPostPageData('');
            setAnlyticsCall(false);
        }
    }, [searchOptions, postTypeHeading]);

    useEffect(() => {
        if (window.IM_SEARCH_DATA?.hasDeclined) {
            localStorage.setItem("dom_ai_consent_declined", "true");
            if (typeof window.applyAIOptOutUI === "function") window.applyAIOptOutUI();
        } else {
            localStorage.removeItem("dom_ai_consent_declined");
            if (typeof window.removeAIOptOutUI === "function") window.removeAIOptOutUI();
        }
    }, []);

    const openSortModal = () => {
        setIsSortActive(!isSortActive);
    };

    const handleChangeSort = async (sortType) => {
        const updatedSearchOpts = { ...searchOptions, sortBy: sortType, loadMore: false };
        await handleUpdates(updatedSearchOpts);
    };

    const handleRemoveFilter = async (category, id) => {
        const updatedSearchOpts = {
            ...searchOptions,
            loadMore: false,
            expandingSearch: false,
            skip: 0,
            take: 8,
            total: 8,
        };

        const arrayName =
            category === 'tech'
                ? 'licensing_Tech'
                : category === 'ms'
                  ? 'products'
                  : category === 'c'
                    ? 'cloud_OnPremises_Hybrid'
                    : category === 'ct'
                      ? 'contentType'
                      : category === 'mt'
                        ? 'mediaType'
                        : category === 'sa'
                          ? 'subjectArea'
                          : category === 'auth'
                            ? 'authors'
                            : category === 'dp'
                              ? 'datePublishedPreset'
                              : category === 'dt'
                                ? 'datePublishedEnd'
                                : category === 'df'
                                  ? 'datePublishedStart'
                                  : category === 'ar'
                                    ? 'archived'
                                    : null;

        switch (arrayName) {
            case 'archived':
                updatedSearchOpts[arrayName] = null;
                break;
            case 'datePublishedEnd':
            case 'datePublishedStart':
            case 'datePublishedPreset':
                updatedSearchOpts[arrayName] = '';
                break;
            default:
                updatedSearchOpts[arrayName] = updatedSearchOpts[arrayName]?.filter(
                    (item) => item !== id
                );
                break;
        }

        await handleUpdates(updatedSearchOpts);
    };

    const handleUpdates = async (updatedSearchOpts) => {
        var currentFilters = JSON.parse(JSON.stringify(allFilters));
        const filters = processContentFilters(searchResults, currentFilters, updatedSearchOpts);
        dispatch(setSearchFilters(filters));

        if (refreshData) refreshData(updatedSearchOpts);
    };

    const handleToggleShowMore = () => {
        setShowMore((prev) => !prev);
    };

    const contentTabHeader = () => {
        return (
            <>
                <div className="mobileFilterBar">
                    <div className="mobileFilterBarGroup">
                        {!searchOptions.isPublicSearch && (
                            <>
                                <button className="mobileFilterBarButton" onClick={filterOpen}>
                                    Filter
                                    <svg
                                        width="16"
                                        height="10"
                                        viewBox="0 0 16 10"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M7.19193 9.58073C7.01484 9.58073 6.86644 9.5208 6.74672 9.40094C6.62686 9.28108 6.56693 9.1326 6.56693 8.95552C6.56693 8.7783 6.62686 8.6299 6.74672 8.51031C6.86644 8.39059 7.01484 8.33073 7.19193 8.33073H8.80255C8.97964 8.33073 9.12811 8.39066 9.24797 8.51052C9.36769 8.63038 9.42755 8.77886 9.42755 8.95594C9.42755 9.13316 9.36769 9.28156 9.24797 9.40115C9.12811 9.52087 8.97964 9.58073 8.80255 9.58073H7.19193ZM3.96276 5.6224C3.78568 5.6224 3.63727 5.56247 3.51755 5.4426C3.39783 5.32274 3.33797 5.17427 3.33797 4.99719C3.33797 4.81997 3.39783 4.67156 3.51755 4.55198C3.63727 4.43226 3.78568 4.3724 3.96276 4.3724H12.0317C12.2088 4.3724 12.3573 4.43233 12.4771 4.55219C12.5969 4.67205 12.6567 4.82052 12.6567 4.9976C12.6567 5.17483 12.5969 5.32323 12.4771 5.44281C12.3573 5.56254 12.2088 5.6224 12.0317 5.6224H3.96276ZM1.54297 1.66406C1.36589 1.66406 1.21748 1.60413 1.09776 1.48427C0.977899 1.36441 0.917969 1.21594 0.917969 1.03885C0.917969 0.861632 0.977899 0.713229 1.09776 0.593646C1.21748 0.473924 1.36589 0.414062 1.54297 0.414062H14.4596C14.6367 0.414062 14.7851 0.473993 14.9048 0.593854C15.0247 0.713715 15.0846 0.862188 15.0846 1.03927C15.0846 1.21649 15.0247 1.3649 14.9048 1.48448C14.7851 1.6042 14.6367 1.66406 14.4596 1.66406H1.54297Z"
                                            fill="#1C1B1F"
                                        ></path>
                                    </svg>
                                </button>
                                <button
                                    className="mobileFilterClear"
                                    onClick={() => handleClearFilter()}
                                >
                                    Clear
                                </button>
                            </>
                        )}
                    </div>

                    <div className="mobileFilterBarGroup">
                        <button className="mobileFilterBarButton" onClick={openSortModal}>
                            Sort
                            <svg
                                width="12"
                                height="16"
                                viewBox="0 0 12 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3.63562 8.53781C3.4584 8.53781 3.30993 8.47788 3.19021 8.35802C3.07062 8.2383 3.01083 8.0899 3.01083 7.91281V2.47052L1.07979 4.40156C0.967014 4.51698 0.826111 4.57469 0.657083 4.57469C0.488055 4.57469 0.338889 4.5149 0.209583 4.39531C0.0802777 4.2649 0.015625 4.1158 0.015625 3.94802C0.015625 3.78038 0.0802777 3.63191 0.209583 3.5026L3.10063 0.611562C3.17854 0.533507 3.26111 0.478507 3.34833 0.446562C3.43569 0.414479 3.52924 0.398438 3.62896 0.398438C3.72868 0.398438 3.8234 0.414549 3.91313 0.446771C4.00285 0.479132 4.08618 0.534062 4.16313 0.611562L7.075 3.52344C7.19889 3.64733 7.26 3.79101 7.25833 3.95448C7.2568 4.11795 7.19139 4.2649 7.06208 4.39531C6.93278 4.5149 6.78639 4.57684 6.62292 4.58115C6.45944 4.58531 6.31313 4.52281 6.18396 4.39365L4.26083 2.47052V7.92073C4.26083 8.09559 4.2009 8.24212 4.08104 8.36031C3.96118 8.47865 3.81271 8.53781 3.63562 8.53781ZM8.37021 15.5955C8.27048 15.5955 8.17576 15.5793 8.08604 15.547C7.99632 15.5148 7.91299 15.4599 7.83604 15.3824L4.92417 12.4705C4.80028 12.3465 4.73917 12.2028 4.74083 12.0395C4.74236 11.876 4.80778 11.7291 4.93708 11.5986C5.06639 11.4791 5.21278 11.4171 5.37625 11.4128C5.53972 11.4085 5.68604 11.471 5.81521 11.6003L7.73833 13.5234V8.07302C7.73833 7.8983 7.79826 7.75177 7.91813 7.63344C8.03799 7.51524 8.18646 7.45615 8.36354 7.45615C8.54076 7.45615 8.68923 7.51601 8.80896 7.63573C8.92854 7.75559 8.98833 7.90406 8.98833 8.08115V13.5234L10.9194 11.5922C11.0322 11.4769 11.1731 11.4193 11.3421 11.4193C11.5111 11.4193 11.6603 11.4791 11.7896 11.5986C11.9189 11.7291 11.9835 11.8781 11.9835 12.0457C11.9835 12.2135 11.9189 12.362 11.7896 12.4914L8.89854 15.3824C8.82063 15.4603 8.73806 15.5153 8.65083 15.5474C8.56347 15.5795 8.46993 15.5955 8.37021 15.5955Z"
                                    fill="#313131"
                                ></path>
                            </svg>
                        </button>
                    </div>
                </div>

                {/* mobile sort modal */}

                <div
                    className={`resultsFiltersSidebar search-sort-modal ${isSortActive ? 'search-sort-modal-open' : ''}`}
                >
                    <div className="resultsFilterCard">
                        <div className="resultsFiltersHeader">
                            <p className="resultsFiltersHeaderTitle">Sort</p>
                            <button
                                className="resultsFiltersClose"
                                title="Close"
                                onClick={openSortModal}
                            >
                                <svg
                                    width="8"
                                    height="8"
                                    viewBox="0 0 8 8"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M3.99896 4.75729L1.34479 7.41146C1.24549 7.51076 1.1191 7.56042 0.965625 7.56042C0.812153 7.56042 0.685764 7.51076 0.586458 7.41146C0.487153 7.31215 0.4375 7.18576 0.4375 7.03229C0.4375 6.87882 0.487153 6.75243 0.586458 6.65313L3.24063 3.99896L0.586458 1.34479C0.487153 1.24549 0.4375 1.1191 0.4375 0.965625C0.4375 0.812153 0.487153 0.685764 0.586458 0.586458C0.685764 0.487153 0.812153 0.4375 0.965625 0.4375C1.1191 0.4375 1.24549 0.487153 1.34479 0.586458L3.99896 3.24063L6.65313 0.586458C6.75243 0.487153 6.87882 0.4375 7.03229 0.4375C7.18576 0.4375 7.31215 0.487153 7.41146 0.586458C7.51076 0.685764 7.56042 0.812153 7.56042 0.965625C7.56042 1.1191 7.51076 1.24549 7.41146 1.34479L4.75729 3.99896L7.41146 6.65313C7.51076 6.75243 7.56042 6.87882 7.56042 7.03229C7.56042 7.18576 7.51076 7.31215 7.41146 7.41146C7.31215 7.51076 7.18576 7.56042 7.03229 7.56042C6.87882 7.56042 6.75243 7.51076 6.65313 7.41146L3.99896 4.75729Z"
                                        fill="white"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                        <div className="resultsFilterbody">
                            <div className="search-sort-list">
                                <ul className="list-unstyled">
                                    <li>
                                        <label htmlFor="MostRelevant">
                                            <input
                                                type="radio"
                                                id="MostRelevant"
                                                name="mobile-sort"
                                                checked={
                                                    !searchOptions.sortBy ||
                                                    searchOptions.sortBy === '' ||
                                                    searchOptions.sortBy === 'relevance'
                                                }
                                                onChange={() => handleChangeSort('relevance')}
                                            />
                                            <span>Most Relevant</span>
                                            <span className="select-check">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M6.81599 9.79115L4.88016 7.8551C4.76474 7.73983 4.61967 7.6808 4.44495 7.67802C4.27036 7.67538 4.12266 7.73441 4.00182 7.8551C3.88113 7.97594 3.82078 8.12233 3.82078 8.29427C3.82078 8.46622 3.88113 8.6126 4.00182 8.73344L6.2887 11.0203C6.43939 11.1709 6.61516 11.2461 6.81599 11.2461C7.01682 11.2461 7.19259 11.1709 7.34328 11.0203L11.9795 6.38406C12.0948 6.26865 12.1538 6.12358 12.1566 5.94885C12.1593 5.77427 12.1002 5.62656 11.9795 5.50573C11.8587 5.38504 11.7123 5.32469 11.5404 5.32469C11.3684 5.32469 11.222 5.38504 11.1012 5.50573L6.81599 9.79115ZM8.00016 15.9193C6.90516 15.9193 5.87592 15.7115 4.91245 15.2959C3.94898 14.8804 3.11092 14.3164 2.39828 13.6041C1.68564 12.8917 1.12141 12.054 0.705573 11.0909C0.289878 10.1279 0.0820312 9.09892 0.0820312 8.00406C0.0820312 6.90906 0.289809 5.87983 0.705365 4.91635C1.12092 3.95288 1.68488 3.11483 2.39724 2.40219C3.1096 1.68955 3.94731 1.12531 4.91036 0.709479C5.87342 0.293785 6.90238 0.0859375 7.99724 0.0859375C9.09224 0.0859375 10.1215 0.293715 11.0849 0.70927C12.0484 1.12483 12.8865 1.68878 13.5991 2.40115C14.3118 3.11351 14.876 3.95122 15.2918 4.91427C15.7075 5.87733 15.9154 6.90628 15.9154 8.00115C15.9154 9.09615 15.7076 10.1254 15.292 11.0889C14.8765 12.0523 14.3125 12.8904 13.6002 13.603C12.8878 14.3157 12.0501 14.8799 11.087 15.2957C10.124 15.7114 9.09502 15.9193 8.00016 15.9193Z"
                                                        fill="#DE3131"
                                                    />
                                                </svg>
                                            </span>
                                        </label>
                                    </li>
                                    <li>
                                        <label htmlFor="MostRecent">
                                            <input
                                                type="radio"
                                                id="MostRecent"
                                                name="mobile-sort"
                                                checked={searchOptions.sortBy === 'date'}
                                                onChange={() => handleChangeSort('date')}
                                            />
                                            <span>Most Recent</span>
                                            <span className="select-check">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M6.81599 9.79115L4.88016 7.8551C4.76474 7.73983 4.61967 7.6808 4.44495 7.67802C4.27036 7.67538 4.12266 7.73441 4.00182 7.8551C3.88113 7.97594 3.82078 8.12233 3.82078 8.29427C3.82078 8.46622 3.88113 8.6126 4.00182 8.73344L6.2887 11.0203C6.43939 11.1709 6.61516 11.2461 6.81599 11.2461C7.01682 11.2461 7.19259 11.1709 7.34328 11.0203L11.9795 6.38406C12.0948 6.26865 12.1538 6.12358 12.1566 5.94885C12.1593 5.77427 12.1002 5.62656 11.9795 5.50573C11.8587 5.38504 11.7123 5.32469 11.5404 5.32469C11.3684 5.32469 11.222 5.38504 11.1012 5.50573L6.81599 9.79115ZM8.00016 15.9193C6.90516 15.9193 5.87592 15.7115 4.91245 15.2959C3.94898 14.8804 3.11092 14.3164 2.39828 13.6041C1.68564 12.8917 1.12141 12.054 0.705573 11.0909C0.289878 10.1279 0.0820312 9.09892 0.0820312 8.00406C0.0820312 6.90906 0.289809 5.87983 0.705365 4.91635C1.12092 3.95288 1.68488 3.11483 2.39724 2.40219C3.1096 1.68955 3.94731 1.12531 4.91036 0.709479C5.87342 0.293785 6.90238 0.0859375 7.99724 0.0859375C9.09224 0.0859375 10.1215 0.293715 11.0849 0.70927C12.0484 1.12483 12.8865 1.68878 13.5991 2.40115C14.3118 3.11351 14.876 3.95122 15.2918 4.91427C15.7075 5.87733 15.9154 6.90628 15.9154 8.00115C15.9154 9.09615 15.7076 10.1254 15.292 11.0889C14.8765 12.0523 14.3125 12.8904 13.6002 13.603C12.8878 14.3157 12.0501 14.8799 11.087 15.2957C10.124 15.7114 9.09502 15.9193 8.00016 15.9193Z"
                                                        fill="#DE3131"
                                                    />
                                                </svg>
                                            </span>
                                        </label>
                                    </li>
                                    <li>
                                        <label htmlFor="MostPopular">
                                            <input
                                                type="radio"
                                                id="MostPopular"
                                                name="mobile-sort"
                                                checked={searchOptions.sortBy === 'popularity'}
                                                onChange={() => handleChangeSort('popularity')}
                                            />
                                            <span>Most Popular</span>
                                            <span className="select-check">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 16 16"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M6.81599 9.79115L4.88016 7.8551C4.76474 7.73983 4.61967 7.6808 4.44495 7.67802C4.27036 7.67538 4.12266 7.73441 4.00182 7.8551C3.88113 7.97594 3.82078 8.12233 3.82078 8.29427C3.82078 8.46622 3.88113 8.6126 4.00182 8.73344L6.2887 11.0203C6.43939 11.1709 6.61516 11.2461 6.81599 11.2461C7.01682 11.2461 7.19259 11.1709 7.34328 11.0203L11.9795 6.38406C12.0948 6.26865 12.1538 6.12358 12.1566 5.94885C12.1593 5.77427 12.1002 5.62656 11.9795 5.50573C11.8587 5.38504 11.7123 5.32469 11.5404 5.32469C11.3684 5.32469 11.222 5.38504 11.1012 5.50573L6.81599 9.79115ZM8.00016 15.9193C6.90516 15.9193 5.87592 15.7115 4.91245 15.2959C3.94898 14.8804 3.11092 14.3164 2.39828 13.6041C1.68564 12.8917 1.12141 12.054 0.705573 11.0909C0.289878 10.1279 0.0820312 9.09892 0.0820312 8.00406C0.0820312 6.90906 0.289809 5.87983 0.705365 4.91635C1.12092 3.95288 1.68488 3.11483 2.39724 2.40219C3.1096 1.68955 3.94731 1.12531 4.91036 0.709479C5.87342 0.293785 6.90238 0.0859375 7.99724 0.0859375C9.09224 0.0859375 10.1215 0.293715 11.0849 0.70927C12.0484 1.12483 12.8865 1.68878 13.5991 2.40115C14.3118 3.11351 14.876 3.95122 15.2918 4.91427C15.7075 5.87733 15.9154 6.90628 15.9154 8.00115C15.9154 9.09615 15.7076 10.1254 15.292 11.0889C14.8765 12.0523 14.3125 12.8904 13.6002 13.603C12.8878 14.3157 12.0501 14.8799 11.087 15.2957C10.124 15.7114 9.09502 15.9193 8.00016 15.9193Z"
                                                        fill="#DE3131"
                                                    />
                                                </svg>
                                            </span>
                                        </label>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {!searchResults.overrideReplacement &&
                    searchResults.replacementQuery &&
                    searchResults.replacementQuery !== '' && (
                        <div className="replacement-text-section">
                            <p className="resultsHeading">
                                Showing Results for {searchResults.replacementQuery}. Search instead
                                for{' '}
                                <a
                                    href={`/atlas/search/?q=${searchResults.searchQuery}&override=true`}
                                >
                                    {searchResults.searchQuery}
                                </a>
                                .
                            </p>
                        </div>
                    )}

                {contentCount > 0 &&
                    searchOptions.searchQuery &&
                    searchOptions.searchQuery != '' && (
                        <span
                            className={`resultsHeading ${!searchResults.overrideReplacement && searchResults.replacementQuery !== '' ? 'results-count-section' : ''}`}
                        >
                            {contentCount === 10000
                                ? `10,000+ Results`
                                : `${formatNumber(contentCount)} Result${contentCount === 1 ? '' : 's'}`}{' '}
                            {!searchResults.overrideReplacement &&
                            searchResults.replacementQuery !== ''
                                ? ''
                                : `for “${searchOptions.searchQuery}”`}
                        </span>
                    )}
                {contentCount > 0 && searchOptions.searchQuery == '' && (
                    <span className="resultsHeading">
                        {contentCount === 10000
                            ? `10,000+ Results`
                            : `${formatNumber(contentCount)} Results`}
                    </span>
                )}

                <div className="resultsFilterBar">
                    {activeFilters && activeFilters.length > 0 && (
                        <div className="resultsFilterWrap">
                            {Array.isArray(activeFilters) &&
                                activeFilters.length > 0 &&
                                activeFilters
                                    ?.slice(0, showMore ? activeFilters.length : 6)
                                    .map((filter, idx) => (
                                        <React.Fragment key={filter.key}>
                                            <button
                                                className=""
                                                onClick={() =>
                                                    handleRemoveFilter(filter.type, filter.id)
                                                }
                                            >
                                                <span>{he.decode(filter.name)}</span>
                                            </button>

                                            {he.decode(filter.name) === 'Webinars' &&
                                                !activeFilters.some(
                                                    (f) => he.decode(f.name) === 'Videos'
                                                ) && (
                                                    <a
                                                        href="/atlas/search/?ct=videos"
                                                        className="custom-button"
                                                        style={{
                                                            padding: '4px 40px',
                                                            minWidth: 'fit-content',
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                        }}
                                                    >
                                                        Videos
                                                    </a>
                                                )}
                                        </React.Fragment>
                                    ))}

                            {activeFilters.length > 6 && (
                                <span className="" onClick={handleToggleShowMore}>
                                    {showMore ? 'Show Less' : '...More'}
                                </span>
                            )}

                            {activeFilters.length > 0 && (
                                <button className="clear" onClick={() => handleClearFilter()}>
                                    Clear All
                                </button>
                            )}
                        </div>
                    )}
                    <div className="resultsSortWrap">
                        <div className="resultsSortSelect">
                            <label for="results-sortby">Sort by:</label>
                            <select
                                id="results-sortby"
                                name="results_orderby"
                                value={searchOptions?.sortBy || 'relevance'}
                                onChange={(e) => handleChangeSort(e.target.value)}
                            >
                                <option value="relevance">Relevancy</option>
                                <option value="date">Most Recent</option>
                                <option value="popularity">Popularity</option>
                            </select>
                        </div>
                        {(gridViewName === 'infographics' ||
                            gridViewName === 'kits_collections') && (
                            <div class="d-inline-flex">
                                <div class="results-view-toggle">
                                    <legend>View</legend>
                                    <input
                                        className="results-view-option grid"
                                        type="radio"
                                        name="list_view_mode"
                                        value="grid"
                                        title="grid"
                                        checked={isGridView ? true : false}
                                        onClick={() => {
                                            dispatch(setIsViewChanged(false));
                                            setIsGridView(true);
                                        }}
                                    />
                                    <input
                                        className="results-view-option list"
                                        type="radio"
                                        name="list_view_mode"
                                        value="list"
                                        title="list"
                                        checked={!isGridView ? true : false}
                                        onClick={() => {
                                            dispatch(setIsViewChanged(true));
                                            setIsGridView(false);
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    };

    const handleClearFilter = async () => {
        refreshData({ searchQuery: '', popularity: true, sortBy: null });
        dispatch(setIsClearFilter(true));
    };

    const handleConsentAgree = async () => {
        try {
            await aiApi.submitConsent(false);
            setConsentOpen(false);
            setRetrySummary(true);
        } catch (err) {
            console.error('Consent save failed', err);
        }
    };

    const handleConsentDecline = async () => {
        try {
            await aiApi.submitConsent(true);
        } catch (err) {
            console.error('Consent decline save failed', err);
        }
        setConsentOpen(false);
        if (window.location.pathname.includes('/atlasassistant')) {
            window.location.href = '/atlas/';
        }
    };
    return (
        <>
            <div className="resultsManagmentSection">
                {/* AI Summary - shows above search results */}
                <AISummary setConsentOpen={setConsentOpen} 
                setPaywallOpen={setPaywallOpen}
                retrySummary={retrySummary}
                setRetrySummary={setRetrySummary}/>

                {anlyticsCall && contentType === 'analyst_reports' && (
                    <div class="index-page-content-head">
                        <div class="index-page-content-head">
                            <div class="index-page-content-head-content">
                                <h1>{dynamicHeading}</h1>
                            </div>
                        </div>
                    </div>
                )}
                {searchOptions?.searchQuery === '' &&
                    searchOptions.contentType &&
                    (searchOptions.contentType.length === 1 || isOnlyWebinarsAndVideos) &&
                    postPageData && (
                        <div
                            className="index-page-content-head"
                            dangerouslySetInnerHTML={{ __html: postPageData }}
                        />
                    )}
                {contentTabHeader()}
                <ContentTab
                    searchResults={searchResults}
                    isLoadMore={isLoadMore}
                    isExpandingSearch={isExpandingSearch}
                    refreshData={refreshData}
                    isGridView={isGridView}
                    gridViewName={gridViewName}
                />
            </div>
            {consentOpen && localStorage.getItem("dom_ai_consent_declined") !== "true" && (
                <ConsentModal onAgree={handleConsentAgree} onDecline={handleConsentDecline} />
            )}
            {paywallOpen && (
                <MembershipPaywallModal />
            )}
        </>
    );
}
